import { localizeSystemText } from '../../../i18n/messages';
import LocalizedMessage from '../../common/LocalizedMessage';
import React from "react";
import PropTypes from "prop-types";
import Input from "../../common/Input";
import SelectInput from "../../common/SelectInput";
import SearchableSelect from "../../common/SearchableSelect";
import { STATUS } from "../../common/Const";
import { PARTICIPATION_STRATEGIES, PAYMENT_STRATEGIES } from "../../billingEnrollment/billingEnrollmentTypes";
import PaymentSchedulePreview from "./PaymentSchedulePreview";
import { getProductDetails } from "./enrollmentDisplay";
import { useTranslation } from 'react-i18next';

const labelForPerson = item => `${item.firstName || ""} ${item.lastName || ""}`.trim() || item.id;
const optionsForPeople = items => items.map(item => ({ value: item.id, text: labelForPerson(item) }));
const optionsForProducts = items => items.map(item => ({ value: item.id, text: item.name || item.id }));
const getProductById = (products, id) => products.find(product => product.id === id) || null;
const LOCKED_PAYMENT_SCHEDULE_STATUSES = ["active", "expired", "depleted", "cancelled"];

function EnrollmentForm({
  clients,
  students,
  products,
  enrollment,
  errors,
  onChange,
  onScheduleItemChange,
  onAddScheduleItem,
  onRemoveScheduleItem,
  onSubmit,
  onCancel,
  status,
}) {
  const { t: tr } = useTranslation();
  const disabled = status === STATUS.SUBMITTING;
  const selectedProduct = getProductById(products, enrollment.productId) || enrollment.product || null;
  const usesSharedStudents = selectedProduct
    && selectedProduct.participationStrategy === PARTICIPATION_STRATEGIES.SHARED_STUDENTS;
  const studentsForSelectedClient = students.filter(student => student.clientId === enrollment.clientId);
  const productDetails = selectedProduct ? getProductDetails(selectedProduct, enrollment.status) : null;
  const usesPaymentSchedule = selectedProduct && selectedProduct.paymentStrategy === PAYMENT_STRATEGIES.INSTALLMENTS;
  const isPaymentScheduleLocked = LOCKED_PAYMENT_SCHEDULE_STATUSES.includes(enrollment.status);

  return (
    <form id="enrollment-form" onSubmit={onSubmit}>
      <h2 className="manage-title">{tr("manage:enrolment_184")}</h2>
      <div className="form-row">
        <div className="col-md-4">
          <SearchableSelect
            label={tr("manage:client_108")}
            name="clientId"
            value={enrollment.clientId || ""}
            placeholder={tr("manage:selectAClient_185")}
            options={optionsForPeople(clients)}
            error={errors.clientId}
            onChange={onChange}
          />
        </div>
        <div className="col-md-4">
          <SelectInput
            label={tr("manage:product_186")}
            name="productId"
            value={enrollment.productId || ""}
            defaultOption="Wybierz produkt"
            options={optionsForProducts(products)}
            error={errors.productId}
            onChange={onChange}
          />
        </div>
        <div className="col-md-4">
          {usesSharedStudents ? (
            <div className="form-group">
              <span><strong>{tr("manage:eligibleStudents_187")}</strong></span>
              <div className="field mt-1">
                <select
                  name="eligibleStudentIds"
                  multiple
                  value={enrollment.eligibleStudentIds || enrollment.studentIds || []}
                  onChange={onChange}
                  className={`form-control ${errors.eligibleStudentIds ? "is-invalid" : ""}`}
                  aria-label={tr("manage:eligibleStudents_187")}
                >
                  {optionsForPeople(studentsForSelectedClient).map(option => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </select>
                {errors.eligibleStudentIds && <div className="invalid-feedback"><LocalizedMessage isError value={errors.eligibleStudentIds} /></div>}
              </div>
            </div>
          ) : (
            <SelectInput
              label={tr("manage:student_188")}
              name="studentIds"
              value={(enrollment.studentIds || enrollment.eligibleStudentIds || [])[0] || ""}
              defaultOption="Wybierz studenta"
              options={optionsForPeople(studentsForSelectedClient)}
              error={errors.studentIds || errors.eligibleStudentIds}
              onChange={onChange}
            />
          )}
        </div>
      </div>
      {productDetails && (
        <section className="mt-2 mb-2">
          <h3 className="manage-title h5">{tr("manage:productSettings_189")}</h3>
          <div className="form-row">
            <div className="col-md-3">
              <Input label={tr("manage:enrolmentStatus_190")} name="enrollmentStatusLabel" value={localizeSystemText(productDetails.status, tr)} readOnly />
            </div>
            <div className="col-md-3">
              <Input label={tr("manage:price_191")} name="productDefaultPrice" value={productDetails.defaultPrice} readOnly />
            </div>
            <div className="col-md-3">
              <Input label={tr("manage:payment_192")} name="productPaymentStrategy" value={localizeSystemText(productDetails.paymentStrategy, tr)} readOnly />
            </div>
            <div className="col-md-3">
              <Input label={tr("manage:charging_193")} name="productChargingStrategy" value={localizeSystemText(productDetails.chargingStrategy, tr)} readOnly />
            </div>
          </div>
          <div className="form-row">
            <div className="col-md-3">
              <Input label={tr("manage:participation_194")} name="productParticipationStrategy" value={localizeSystemText(productDetails.participationStrategy, tr)} readOnly />
            </div>
            <div className="col-md-3">
              <Input label={tr("manage:numberOfLessons_195")} name="productPackageUnits" value={productDetails.packageUnits} readOnly />
            </div>
            <div className="col-md-3">
              <Input label={tr("manage:lessonType_196")} name="productClassType" value={productDetails.classType} readOnly />
            </div>
            <div className="col-md-3">
              <Input label={tr("manage:validity_197")} name="productValidity" value={selectedProduct.validForDays ? tr("manage:attendance.days", { count: Number(selectedProduct.validForDays) }) : localizeSystemText(productDetails.validity, tr)} readOnly />
            </div>
          </div>
        </section>
      )}
      <div className="form-row">
        <div className="col-md-3">
          <Input
            label={tr("manage:start_198")}
            name="startDate"
            type="date"
            value={enrollment.startDate || ""}
            error={errors.startDate}
            onChange={onChange}
          />
        </div>
      </div>
      {usesPaymentSchedule && (
      <section className="mt-2 mb-2">
        <h3 className="manage-title h5">{tr("manage:paymentSchedule_199")}</h3>
        <p className="manage-text mb-2">{tr("manage:thisScheduleShowsTheChargesArisingFromTheEnrolment_200")}</p>
      {!isPaymentScheduleLocked && (
        <button
          type="button"
          className="btn manage-button manage-teal-button mt-2"
          disabled={disabled}
          onClick={onAddScheduleItem}
        >{tr("manage:addScheduledPayment_201")}</button>
      )}
      <PaymentSchedulePreview
        items={enrollment.paymentScheduleItems || []}
        onChange={onScheduleItemChange}
        onRemove={onRemoveScheduleItem}
        readOnly={isPaymentScheduleLocked}
      />
      </section>
      )}
      <div className="pt-4 row mx-0">
        <div className="ml-auto col-md-block">
          <button type="submit" className="btn manage-button manage-red-button btn-block" disabled={disabled}>
            {disabled ? "Zapisywanie..." : tr("common:save")}
          </button>
        </div>
        <div className="col-md-block pl-2 pr-2">
          <button type="button" className="btn manage-button manage-teal-button btn-block" disabled={disabled} onClick={onCancel}>{tr("manage:back_128")}</button>
        </div>
      </div>
    </form>
  );
}

EnrollmentForm.propTypes = {
  clients: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  products: PropTypes.array.isRequired,
  enrollment: PropTypes.object.isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onScheduleItemChange: PropTypes.func,
  onAddScheduleItem: PropTypes.func,
  onRemoveScheduleItem: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  status: PropTypes.string,
};

EnrollmentForm.defaultProps = {
  errors: {},
  onScheduleItemChange: () => {},
  onAddScheduleItem: () => {},
  onRemoveScheduleItem: () => {},
  onCancel: () => {},
  status: STATUS.IDLE,
};

export default EnrollmentForm;
